import { locale } from '../../consts';
import { Endpoint } from '../../models/api.model';
import { AvailableBrands } from '../../components/Itv/types';
import { SubNav } from '../../layouts/pages/ItvMain';
import { getImageUrl } from '../../utils/navigation';
import {
  GetSubNavigationQuery,
  useGetSubNavigationQuery,
} from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-sub-nav.query.generated';
import {
  GetMobileSubNavQuery,
  useGetMobileSubNavQuery,
} from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-mobile-sub-nav.query.generated';

type MobileNav = NonNullable<GetMobileSubNavQuery['mobile_nav']>;
type BingoNavItem = NonNullable<MobileNav['bingo_nav']>[number];
type SpinsNavItem = NonNullable<MobileNav['spins_nav']>[number];
type NavItem = GetSubNavigationQuery['bingo'][number] | GetSubNavigationQuery['spins'][number];

const toSubNav = (navItem: NavItem, brand: string, index: number): SubNav | null => {
  const [data] = navItem.translations || [];

  if (!data) return null;

  const iconUrl = navItem.icon?.filename_disk ? getImageUrl(navItem.icon.filename_disk) : '';
  const firstCollection = navItem.collections?.[0]?.Collections_id?.translations?.[0]?.slug;

  return {
    slug: index > 0 ? `/${brand}/${data.slug}` : `/${brand}`,
    extraQueryParams: navItem.view_all_first_collection ? `?viewAll=${firstCollection}` : '',
    title: data.title,
    img: iconUrl,
  };
};

const transformDesktopNav = (subNavigation: GetSubNavigationQuery | undefined, brand: AvailableBrands): SubNav[] => {
  const brandItems = subNavigation?.[brand];

  if (!brandItems) return [];

  return brandItems
    .map((navItem, index) => toSubNav(navItem, brand, index))
    .filter((item): item is SubNav => item !== null);
};

const transformMobileNav = (
  mobileNav: GetMobileSubNavQuery['mobile_nav'] | undefined | null,
  brand: AvailableBrands
): SubNav[] => {
  if (!mobileNav) return [];

  const items = brand === 'bingo' ? mobileNav.bingo_nav : mobileNav.spins_nav;

  if (!items) return [];

  return items
    .map((junctionItem, index) => {
      const navItem =
        brand === 'bingo' ? (junctionItem as BingoNavItem)?.Bingo_id : (junctionItem as SpinsNavItem)?.spins_id;

      if (!navItem || navItem.status !== 'published') return null;

      return toSubNav(navItem, brand, index);
    })
    .filter((item): item is SubNav => item !== null);
};

interface UseSubNavReturn {
  subNavigation: GetSubNavigationQuery | undefined;
  subNav: SubNav[];
  mobileSubNav: SubNav[] | undefined;
}

export const useSubNav = (brand: AvailableBrands): UseSubNavReturn => {
  const { data: subNavigation } = useGetSubNavigationQuery({
    variables: { language: locale },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const { data: mobileSubNavigation } = useGetMobileSubNavQuery({
    variables: { language: locale },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const subNav = transformDesktopNav(subNavigation, brand);
  const mobileItems = transformMobileNav(mobileSubNavigation?.mobile_nav, brand);

  return {
    subNavigation,
    subNav,
    mobileSubNav: mobileItems.length > 0 ? mobileItems : undefined,
  };
};
